import Enumerable from "linq";
import { Filter, FilterType } from "../metadata/metadata.module";
import { addDays, addYears, addMonths, addWeeks, format } from "date-fns";
import { Identity } from "../authentication/authSlice";

export const tendersLinqPropertyAccessors = (fieldKey: string) => {
	switch (fieldKey) {
		case "installed_capacity_mw":
			return (item: any) => item.installed_capacity_mw;
		case "deadline":
			return (item: any) =>
				new Date(`${item.due_date}T00:00:00`).getTime();
		case "validity_date":
			return (item: any) =>
				new Date(`${item.validity_date}T00:00:00`).getTime();
		case "name":
			return (item: any) => item.name.toLowerCase();
		case "account":
			return (item: any) => item.account.name.toLowerCase();
		case "country":
			return (item: any) =>
				item.countries.length >= 0
					? item.countries[0].toLowerCase()
					: "";
		case "tenders_status":
			return (item: any) => item.status.toLowerCase();
		case "tenders_status_not_in":
			return (item: any) => item.status.toLowerCase();
		case "pricings_status":
			return (item: any) => item.pricing_statuses || [];
		case "technology":
			return (item: any) =>
				item.technologies.length >= 0
					? item.technologies[0].toLowerCase()
					: "";
		case "originator":
			return (item: any) => item.originator.id.toString();
		case "trader":
			return (item: any) => item.trader.id.toString();
		case "archived":
			return (item: any) => item.archived.toString();
	}
	return (item: any) => item[fieldKey];
};

export const workflowViewLinqPropertyAccessors = (fieldKey: string) => {
	switch (fieldKey) {
		case "deadline":
			return (item: any) =>
				new Date(`${item.due_date}T00:00:00`).getTime();
		case "country":
			return (item: any) =>
				item.countries.map((c: any) => c.toString().toLowerCase());
		case "pricing_stage":
			return (item: any) => item.pricing_stage.toLowerCase();
	}
	return (item: any) => item[fieldKey];
};

export const pricingsLinqPropertyAccessors = (fieldKey: string) => {
	switch (fieldKey) {
		case "status":
			return (item: any) => item.status.toLowerCase();
		case "status_not_in":
			return (item: any) => item.status.toLowerCase();
		case "result_option":
			return (item: any) => item.result_option.toLowerCase();
		case "portfolio":
			return (item: any) =>
				item.portfolio_id ? item.portfolio_id.toString() : "";
		case "party":
			return (item: any) =>
				item.party ? item.party.name.toLowerCase() : "";
		case "parties":
			return (item: any) =>
				item.parties.map((pricingParty: any) =>
					pricingParty.party_id.toString()
				);
		case "pricing_date":
			return (item: any) =>
				new Date(`${item.pricing_date}T00:00:00`).getTime();
		case "validity_date":
			return (item: any) =>
				new Date(`${item.validity_date}T00:00:00`).getTime();
		case "pricing_type":
			return (item: any) => item.pricing_type_id.toString();
		case "delivery-periods":
			return (item: any) => item.delivery_period.toString().toLowerCase();
		case "site":
			return (item: any) => item.site_pricings[0]?.site?.id.toString();
		case "technology":
			return (item: any) => item.technology_id.toString();
		case "technology_group":
			return (item: any) => item.technology_id;
		case "in_use":
			return (item: any) => item.in_use.toString();
		case "pricings_status":
		case "pricings_status_not_in":
			return (item: any) => item.status.toLowerCase() || "";
	}
	return (item: any) => item[fieldKey];
};

export const linqPropertyAccessors = (page: string, fieldKey: string) => {
	if (page === "Tenders Workflow view") {
		return workflowViewLinqPropertyAccessors(fieldKey);
	}

	if (page === "Tenders") {
		return tendersLinqPropertyAccessors(fieldKey);
	}

	if (page === "Pricings") {
		return pricingsLinqPropertyAccessors(fieldKey);
	}

	return (item: any) => item[fieldKey];
};

export const isMatchingAgainstArray = (page: string, fieldKey: string) => {
	if (page === "Tenders") {
		if (fieldKey === "pricings_status") {
			return true;
		}
	}

	if (page === "Pricings") {
		if (fieldKey === "parties") {
			return true;
		}
	}

	if (page === "Tenders Workflow view") {
		if (fieldKey === "country") {
			return true;
		}
	}

	return false;
};

export const linqValueAccessors = (
	page: string,
	fieldKey: string,
	values: any[]
) => {
	if (page === "Tenders") {
		switch (fieldKey) {
			case "technology":
				return values.map((m: any) => m.name.toLowerCase());
		}
	}

	if (page === "Pricings") {
		switch (fieldKey) {
			case "technology_group":
				return values.map((m: any) => m.toString());
		}
	}
	return values.map((m: any) => m.id.toString().toLowerCase());
};

export const convertValue = (f: Filter, val: Date | string | number) => {
	if (f.filter_type === FilterType.date) {
		const asDate = val instanceof Date ? val : new Date(val);
		return asDate.toISOString().split("T")[0];
	}

	return val;
};

export const convertToLinqValue = (f: Filter, val: Date | string | number) => {
	if (f.filter_type === FilterType.date) {
		const sDate = new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "numeric",
			day: "numeric",
		}).format(new Date(val));
		return new Date(sDate).getTime();
	}

	return Number(val);
};

export const getNumericPredicate = (
	page: string,
	f: Filter,
	val: string[] | string
) => {
	const propertyAccessor = linqPropertyAccessors(page, f.field_key);
	if (val && !Array.isArray(val)) {
		const predicate = `${f.field_path} eq ${convertValue(f, val)}`;
		const linqPredicate = (item: any) => {
			return propertyAccessor(item) === convertToLinqValue(f, val);
		};
		return { predicate, linqPredicate };
	}

	if (val[0] && val[1]) {
		const predicate = `(${f.field_path} ge ${convertValue(f, val[0])} and ${
			f.field_path
		} le ${convertValue(f, val[1])})`;
		const linqPredicate = (item: any) =>
			propertyAccessor(item) >= convertToLinqValue(f, val[0]) &&
			propertyAccessor(item) <= convertToLinqValue(f, val[1]);

		return { predicate, linqPredicate };
	}

	if (val[0]) {
		const predicate = `${f.field_path} ge ${convertValue(f, val[0])}`;
		const linqPredicate = (item: any) =>
			propertyAccessor(item) >= convertToLinqValue(f, val[0]);
		return { predicate, linqPredicate };
	}

	if (val[1]) {
		const predicate = `${f.field_path} le ${convertValue(f, val[1])}`;
		const linqPredicate = (item: any) =>
			propertyAccessor(item) <= convertToLinqValue(f, val[1]);
		return { predicate, linqPredicate };
	}
};

export const getListPredicate = (f: Filter, val: any[]) => {
	let ids = val.map((v: any) => {
		if (Array.isArray(v.id)) {
			return v.id.map((e: any) => `'${e}'`);
		}
		return `'${v.id ?? v}'`;
	});

	if (ids.length > 0) {
		if (Array.isArray(ids[0])) {
			ids = ids[0];
		}
		if (ids.length === 1) {
			if (
				f.filter_type === FilterType.list_not_in ||
				f.filter_type === FilterType.list_input_not_in
			) {
				return `${f.field_path} in (${ids
					.map(encodeURIComponent)
					.join(",")},) eq false`;
			} else {
				return `${f.field_path} in (${ids
					.map(encodeURIComponent)
					.join(",")},)`;
			}
		}
		if (
			f.filter_type === FilterType.list_not_in ||
			f.filter_type === FilterType.list_input_not_in
		) {
			return `${f.field_path} in (${ids
				.map(encodeURIComponent)
				.join(",")}) eq false`;
		} else {
			return `${f.field_path} in (${ids
				.map(encodeURIComponent)
				.join(",")})`;
		}
	}
};

export const applyLinqPredicates = (items: any[], predicates: any[]): any[] => {
	let query = Enumerable.from(items);
	predicates.forEach((predicate) => {
		query = query.where(predicate);
	});
	return query.toArray();
};

export const getExactLinqPredicate = (
	page: string,
	filter: Filter,
	value: any
) => {
	const propertyAccessor = linqPropertyAccessors(page, filter.field_key);
	return (item: any) => propertyAccessor(item) === value;
};

export const getAlphaNumericLingPredicate = (
	page: string,
	filter: Filter,
	value: string
) => {
	const propertyAccessor = linqPropertyAccessors(page, filter.field_key);
	if (page === "Pricings") {
		switch (filter.field_key) {
			case "site":
				return (item: any) =>
					item.site_pricings.some((site_pricing: any) =>
						site_pricing.site.name
							.toLowerCase()
							.includes(value.toLowerCase())
					);
		}
	}
	return (item: any) => {
		return ("" + propertyAccessor(item))
			.toLowerCase()
			.includes(("" + value).toLowerCase());
	};
};

export type LinqPredicate = (item: any) => boolean;

export function buildExactPredicate(
	f: Filter,
	val: any,
	predicates: string[],
	linqPredicates: any[],
	page: string
) {
	const predicate = `${f.field_path} eq ${encodeURIComponent(
		JSON.stringify(val)
	)}`;
	predicates.push(predicate);
	linqPredicates.push(getExactLinqPredicate(page, f, val));
}

export function buildBoolPredicate(
	f: Filter,
	val: any,
	predicates: string[],
	linqPredicates: any[],
	page: string
) {
	const boolValue = val ? "True" : "False";
	const predicate = `${f.field_path} eq ${boolValue}`;
	predicates.push(predicate);
	linqPredicates.push(getAlphaNumericLingPredicate(page, f, val));
}

export function buildAlphaNumericPredicate(
	val: any,
	f: Filter,
	predicates: string[],
	linqPredicates: any[],
	page: string
) {
	if (val) {
		const predicate = `contains(tolower(${
			f.field_path
		}), '${encodeURIComponent("" + val).toLowerCase()}')`;
		predicates.push(predicate);
		linqPredicates.push(getAlphaNumericLingPredicate(page, f, val));
	}
}

export function buildListInputNotInPredicate(
	val: any,
	f: Filter,
	page: string,
	predicates: string[],
	linqPredicates: any[]
) {
	if (val) {
		const valInput = val.replace(" ", "").split(",");
		const predicate = getListPredicate(f, valInput);
		const propertyAccessor = linqPropertyAccessors(page, f.field_key);
		if (predicate) {
			predicates.push(predicate);
			linqPredicates.push(
				(item: any) => !valInput.includes(propertyAccessor(item))
			);
		}
	}
}

export function buildListInputPredicate(
	val: any,
	f: Filter,
	page: string,
	predicates: string[],
	linqPredicates: any[]
) {
	if (val) {
		const valInput = val.replace(" ", "").split(",");
		const predicate = getListPredicate(f, valInput);
		const propertyAccessor = linqPropertyAccessors(page, f.field_key);
		if (predicate) {
			predicates.push(predicate);
			linqPredicates.push((item: any) =>
				valInput.includes(propertyAccessor(item))
			);
		}
	}
}

export function buildListInPredicate(
	linqPredicates: any[],
	page: string,
	f: Filter,
	val: any,
	propertyAccessor: (item: any) => any
) {
	linqPredicates.push((item: any) =>
		linqValueAccessors(page, f.field_key, val).includes(
			propertyAccessor(item)
		)
	);
}

export function buildListNotInPredicate(
	linqPredicates: any[],
	page: string,
	f: Filter,
	val: any,
	propertyAccessor: (item: any) => any
) {
	linqPredicates.push(
		(item: any) =>
			!linqValueAccessors(page, f.field_key, val).includes(
				propertyAccessor(item)
			)
	);
}

export function buildListInValArrayPredicate(
	linqPredicates: any[],
	page: string,
	f: Filter,
	val: any,
	propertyAccessor: (item: any) => any
) {
	linqPredicates.push((item: any) => {
		return (
			linqValueAccessors(page, f.field_key, val).filter(
				Set.prototype.has,
				new Set(propertyAccessor(item))
			).length > 0
		);
	});
}

export function buildListNotInValArrayPredicate(
	linqPredicates: any[],
	page: string,
	f: Filter,
	val: any,
	propertyAccessor: (item: any) => any
) {
	linqPredicates.push(
		(item: any) =>
			linqValueAccessors(page, f.field_key, val).filter(
				Set.prototype.has,
				new Set(propertyAccessor(item))
			).length === 0
	);
}

export function buildDatePredicate(
	page: string,
	f: Filter,
	val: any,
	predicates: string[],
	linqPredicates: any[]
) {
	const numericPredicates = getNumericPredicate(page, f, val ?? []);
	if (numericPredicates?.predicate) {
		predicates.push(numericPredicates.predicate);
	}
	if (numericPredicates?.linqPredicate) {
		linqPredicates.push(numericPredicates.linqPredicate);
	}
}

export function buildNumericPredicate(
	page: string,
	f: Filter,
	val: any,
	predicates: string[],
	linqPredicates: any[]
) {
	const numericPredicates = getNumericPredicate(page, f, val);
	if (numericPredicates?.predicate) {
		predicates.push(numericPredicates.predicate);
	}
	if (numericPredicates?.linqPredicate) {
		linqPredicates.push(numericPredicates.linqPredicate);
	}
}

export function removeEmptyListsAndNullValues(entryDict: object) {
	return Object.fromEntries(
		Object.entries(entryDict).filter(([, v]) => {
			if (v instanceof Array) return v.length > 0;
			return Boolean(v);
		})
	);
}

export function buildListPredicate(
	val: any,
	page: string,
	f: Filter,
	predicates: string[],
	linqPredicates: any[]
) {
	if (val) {
		const propertyAccessor = linqPropertyAccessors(page, f.field_key);
		const predicate = getListPredicate(f, val);
		if (predicate) {
			predicates.push(predicate);
			const isValArray = isMatchingAgainstArray(page, f.field_key);
			if (isValArray) {
				if (f.filter_type === FilterType.list_not_in) {
					buildListNotInValArrayPredicate(
						linqPredicates,
						page,
						f,
						val,
						propertyAccessor
					);
				} else {
					buildListInValArrayPredicate(
						linqPredicates,
						page,
						f,
						val,
						propertyAccessor
					);
				}
			} else if (f.filter_type === FilterType.list_not_in) {
				buildListNotInPredicate(
					linqPredicates,
					page,
					f,
					val,
					propertyAccessor
				);
			} else {
				buildListInPredicate(
					linqPredicates,
					page,
					f,
					val,
					propertyAccessor
				);
			}
		}
	}
}

export const buildPredicates = (
	filtersData: Filter[],
	values: any,
	page: string
): { predicates: string[]; linqPredicates: LinqPredicate[] } => {
	const predicates: string[] = [];
	const linqPredicates: any[] = [];
	filtersData.forEach((f: Filter) => {
		if (!values.hasOwnProperty(f.field_key)) {
			return;
		}

		const val: any = values[f.field_key];
		if (f.filter_type === FilterType.numeric) {
			buildNumericPredicate(page, f, val, predicates, linqPredicates);
		}
		if (f.filter_type === FilterType.date) {
			buildDatePredicate(page, f, val, predicates, linqPredicates);
		}
		if (
			f.filter_type === FilterType.list ||
			f.filter_type === FilterType.list_not_in
		) {
			buildListPredicate(val, page, f, predicates, linqPredicates);
		}
		if (f.filter_type === FilterType.list_input) {
			buildListInputPredicate(val, f, page, predicates, linqPredicates);
		}
		if (f.filter_type === FilterType.list_input_not_in) {
			buildListInputNotInPredicate(
				val,
				f,
				page,
				predicates,
				linqPredicates
			);
		}
		if (f.filter_type === FilterType.alphanumeric) {
			buildAlphaNumericPredicate(
				val,
				f,
				predicates,
				linqPredicates,
				page
			);
		}
		if (f.filter_type === FilterType.exact) {
			buildExactPredicate(f, val, predicates, linqPredicates, page);
		}
		if (f.filter_type === FilterType.bool) {
			buildBoolPredicate(f, val, predicates, linqPredicates, page);
		}

	});

	return {
		predicates,
		linqPredicates,
	};
};

const getDateFromPlaceHolder = (placeHolder: string): string => {
	// | separator means add, ! means remove
	const parts = placeHolder.split(/[|!]+/);
	const operator = placeHolder.split("!").length === 2 ? -1 : 1;
	if (parts.length === 2) {
		const years = parts[1].split("Y");
		if (years.length === 2) {
			const formattedDate = format(
				addYears(new Date(), operator * Number(years[0])),
				"yyyy-MM-dd'T'HH:mm:ss'Z'"
			);
			return `"${formattedDate}"`;
		}
		const months = parts[1].split("M");
		if (months.length === 2) {
			const formattedDate = format(
				addMonths(new Date(), operator * Number(months[0])),
				"yyyy-MM-dd'T'HH:mm:ss'Z'"
			);
			return `"${formattedDate}"`;
		}
		const weeks = parts[1].split("W");
		if (weeks.length === 2) {
			const formattedDate = format(
				addWeeks(new Date(), operator * Number(weeks[0])),
				"yyyy-MM-dd'T'HH:mm:ss'Z'"
			);
			return `"${formattedDate}"`;
		}

		const formattedDate = format(
			addDays(new Date(), operator * Number(parts[1])),
			"yyyy-MM-dd'T'HH:mm:ss'Z'"
		);
		return `"${formattedDate}"`;
	}

	const formattedDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'");
	return `"${formattedDate}"`;
};

export const parseFromTemplate = (
	template: string,
	identity?: Identity
): string => {
	const dates = template.match(/"(NOW\(\).*?)"/g);
	dates?.forEach((line) => {
		const placeHolder = line.replace(/(^"|"$)/g, "");
		template = template.replace(line, getDateFromPlaceHolder(placeHolder));
	});

	if (identity) {
		const userPlaceHolder = template.match(/"(USER\(\).*?)"/g);
		userPlaceHolder?.forEach((match) => {
			template = template.replace(
				match,
				`{ "id": ${identity.id}, "name": "${identity.first_name} ${identity.last_name}"}`
			);
		});
	}

	return template;
};

export const setSelectedFilterValues = (
	filtersData: Filter[],
	storedFilters: string
) => {
	let obj: { [k: string]: any } = {};

	if (!storedFilters) {
		return obj;
	}

	if (storedFilters) {
		obj = JSON.parse(storedFilters);
		filtersData.forEach((f: Filter) => {
			if (f.filter_type === FilterType.date) {
				const values = obj[f.field_key]?.map((v: string) => {
					if (v) {
						return new Date(v);
					}
					return v;
				});
				obj[f.field_key] = values;
			}
		});

		return obj;
	}
};
